
import { StandardDialog } from "@/components/ui/standard-dialog";
import { Button } from "@/components/ui/button";
import { LifeBuoy } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";

interface Event {
  event_id: string;
  name: string;
  event_date: string | null;
  status: string;
}

export const SupportDialog = () => {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [description, setDescription] = useState("");
  const whatsappNumber = "+61410568925";

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const { data, error } = await supabase
        .from('events')
        .select('event_id, name, event_date, status')
        .eq('archived', false)
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;
      setEvents(data || []);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSelectedEvent = () => {
    return events.find(event => event.event_id === selectedEventId);
  };

  const constructWhatsAppMessage = () => {
    const selectedEvent = getSelectedEvent();
    let message = "Hi! I need help with ";

    if (selectedEvent) {
      message += `the event: ${selectedEvent.name}\n`;
      message += `Event ID: ${selectedEvent.event_id}\n`;
      if (selectedEvent.event_date) {
        message += `Date: ${format(new Date(selectedEvent.event_date), 'PPP')}\n`;
      }
      message += `Status: ${selectedEvent.status}\n\n`;
    }

    message += `Issue Description:\n${description}`;
    return encodeURIComponent(message);
  };

  const isValid = description.length >= 10 && description.length <= 200;
  const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${constructWhatsAppMessage()}`;
  const remainingChars = 200 - description.length;

  return <StandardDialog 
    trigger={<Button variant="ghost" size="icon" className="text-gray-600">
      <LifeBuoy size={24} />
    </Button>} 
    title="Need help?"
  >
      <div className="space-y-6">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Which event do you need help with? (Optional)</Label>
            <Select
              value={selectedEventId || "general"}
              onValueChange={(value) => setSelectedEventId(value === "general" ? null : value)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select an event" />
              </SelectTrigger>
              <SelectContent className="bg-white border shadow-lg">
                <SelectItem value="general" className="hover:bg-gray-100">General Support</SelectItem>
                {events.map((event) => (
                  <SelectItem 
                    key={event.event_id} 
                    value={event.event_id}
                    className="hover:bg-gray-100"
                  >
                    {event.name} {event.event_date && `(${format(new Date(event.event_date), 'PP')})`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">What do you need help with?</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Please describe your issue..."
              className="min-h-[100px] resize-none"
              maxLength={200}
            />
            <p className="text-sm text-muted-foreground">
              {remainingChars} characters remaining
            </p>
          </div>

          <p className="text-sm text-muted-foreground">
            Support hours: Monday-Friday, 9am-6pm PT
          </p>
        </div>

        <Button 
          onClick={() => window.open(whatsappUrl, '_blank')} 
          className="w-full flex items-center justify-center gap-2 bg-[#29b53e]"
          disabled={!isValid}
        >
          <img src="/lovable-uploads/f5285f76-466a-4695-97f4-5e5917d60b11.png" alt="WhatsApp" className="w-5 h-5" />
          {isValid ? "Chat with us on WhatsApp" : "Please provide more details (min 10 characters)"}
        </Button>
      </div>
    </StandardDialog>;
};
